import { useEffect, useState, useRef } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  voiceStore,
  fetchVoices,
  cloneVoice,
  deleteVoice,
  generateSpeech,
  playVoicePreview,
  type Voice,
  type VoiceCloneRequest,
} from "../stores/voices";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  AlertCircle,
  Plus,
  Trash2,
  Play,
  Upload,
  Mic,
  Volume2,
  Download,
  Loader2,
} from "lucide-react";

// =============================================================================
// Voice Clone Dialog
// =============================================================================

interface VoiceCloneDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (request: VoiceCloneRequest) => Promise<void>;
}

function VoiceCloneDialog({
  open,
  onOpenChange,
  onSubmit,
}: VoiceCloneDialogProps) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [audioFiles, setAudioFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { cloneProgress } = useSnapshot(voiceStore);

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Filter for audio files only
    const audioFiles = files.filter(
      (f) =>
        f.type.startsWith("audio/") ||
        f.name.match(/\.(mp3|wav|m4a|ogg|flac)$/i),
    );
    setAudioFiles((prev) => [...prev, ...audioFiles]);
  };

  const removeFile = (index: number) => {
    setAudioFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (audioFiles.length === 0) {
      toast.error("Please upload at least one audio sample");
      return;
    }
    setLoading(true);
    try {
      await onSubmit({ name, description, audioFiles });
      onOpenChange(false);
      setName("");
      setDescription("");
      setAudioFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const totalSize = audioFiles.reduce((sum, f) => sum + f.size, 0);
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Clone Voice</DialogTitle>
          <DialogDescription>
            Upload audio samples of the voice you want to clone. For best
            results, use clear recordings with minimal background noise.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="voice-name">Voice Name</Label>
            <Input
              id="voice-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Custom Voice"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="voice-description">Description (optional)</Label>
            <Textarea
              id="voice-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the voice characteristics..."
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Audio Samples</Label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                MP3, WAV, M4A, OGG, FLAC (max 25MB total)
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.ogg,.flac"
              multiple
              onChange={handleFilesChange}
              className="hidden"
            />

            {audioFiles.length > 0 && (
              <div className="mt-3 space-y-2">
                {audioFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-muted px-3 py-2 rounded-md"
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <Mic className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatSize(file.size)})
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Total: {audioFiles.length} file(s), {formatSize(totalSize)}
                </p>
              </div>
            )}
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Requirements</AlertTitle>
            <AlertDescription className="text-sm">
              <ul className="list-disc list-inside space-y-1 mt-1">
                <li>At least 1 minute of clear speech recommended</li>
                <li>Minimal background noise</li>
                <li>Consistent audio quality across samples</li>
                <li>Only clone voices you have permission to use</li>
              </ul>
            </AlertDescription>
          </Alert>

          {cloneProgress !== null && (
            <div className="space-y-2">
              <Progress value={cloneProgress} />
              <p className="text-sm text-center text-muted-foreground">
                {cloneProgress < 30
                  ? "Uploading samples..."
                  : cloneProgress < 70
                    ? "Processing voice..."
                    : "Finalizing..."}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading || audioFiles.length === 0}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cloning...
                </>
              ) : (
                "Clone Voice"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// TTS Test Dialog
// =============================================================================

interface TTSTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  voice: Voice | null;
}

function TTSTestDialog({ open, onOpenChange, voice }: TTSTestDialogProps) {
  const [text, setText] = useState(
    "Hello! This is a test of the voice cloning feature.",
  );
  const [stability, setStability] = useState(0.5);
  const [similarity, setSimilarity] = useState(0.75);
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const handleGenerate = async () => {
    if (!voice || !text.trim()) return;

    setLoading(true);
    setAudioUrl(null);

    try {
      const blob = await generateSpeech(text, voice.voice_id, {
        stability,
        similarity_boost: similarity,
      });

      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      toast.success("Speech generated!");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to generate speech");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!audioUrl) return;
    const a = document.createElement("a");
    a.href = audioUrl;
    a.download = `${voice?.name || "tts"}-output.mp3`;
    a.click();
  };

  // Cleanup URL on close
  useEffect(() => {
    if (!open && audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Test Voice: {voice?.name}</DialogTitle>
          <DialogDescription>
            Generate speech using this voice to test how it sounds.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="tts-text">Text to speak</Label>
            <Textarea
              id="tts-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter text to convert to speech..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label>Stability: {stability.toFixed(2)}</Label>
            <Slider
              value={[stability]}
              onValueChange={([v]) => setStability(v)}
              min={0}
              max={1}
              step={0.05}
            />
            <p className="text-xs text-muted-foreground">
              Higher values = more consistent, lower = more expressive
            </p>
          </div>

          <div className="space-y-2">
            <Label>Similarity Boost: {similarity.toFixed(2)}</Label>
            <Slider
              value={[similarity]}
              onValueChange={([v]) => setSimilarity(v)}
              min={0}
              max={1}
              step={0.05}
            />
            <p className="text-xs text-muted-foreground">
              Higher values = closer to original voice
            </p>
          </div>

          {audioUrl && (
            <div className="space-y-2">
              <Label>Generated Audio</Label>
              <div className="flex items-center gap-2">
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  controls
                  className="flex-1"
                />
                <Button variant="outline" size="icon" onClick={handleDownload}>
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button onClick={handleGenerate} disabled={loading || !text.trim()}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Volume2 className="h-4 w-4 mr-2" />
                Generate Speech
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Voice Card Component
// =============================================================================

interface VoiceCardProps {
  voice: Voice;
  onTest: (voice: Voice) => void;
  onDelete: (voice: Voice) => void;
}

function VoiceCard({ voice, onTest, onDelete }: VoiceCardProps) {
  const canDelete = voice.category === "cloned";

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{voice.name}</CardTitle>
            {voice.description && (
              <CardDescription className="mt-1 line-clamp-2">
                {voice.description}
              </CardDescription>
            )}
          </div>
          <Badge
            variant={
              voice.category === "cloned"
                ? "default"
                : voice.category === "premade"
                  ? "secondary"
                  : "outline"
            }
          >
            {voice.category}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {voice.labels && Object.keys(voice.labels).length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {Object.entries(voice.labels).map(([key, value]) => (
              <Badge key={key} variant="outline" className="text-xs">
                {key}: {value}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          {voice.preview_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => playVoicePreview(voice)}
            >
              <Play className="h-4 w-4 mr-1" />
              Preview
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onTest(voice)}>
            <Volume2 className="h-4 w-4 mr-1" />
            Test
          </Button>
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onDelete(voice)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Main Voices Page
// =============================================================================

export default function Voices() {
  const { voices, loading, error } = useSnapshot(voiceStore);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [ttsDialogOpen, setTtsDialogOpen] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState<Voice | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchVoices().catch(() => {
      // Error already set in store
    });
  }, []);

  const handleCloneVoice = async (request: VoiceCloneRequest) => {
    try {
      await cloneVoice(request);
      toast.success(`Voice "${request.name}" cloned successfully!`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to clone voice");
      throw e;
    }
  };

  const handleTestVoice = (voice: Voice) => {
    setSelectedVoice(voice as Voice);
    setTtsDialogOpen(true);
  };

  const handleDeleteClick = (voice: Voice) => {
    setVoiceToDelete(voice as Voice);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!voiceToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteVoice(voiceToDelete.voice_id);
      toast.success(`Voice "${voiceToDelete.name}" deleted`);
      setDeleteDialogOpen(false);
      setVoiceToDelete(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete voice");
    } finally {
      setDeleteLoading(false);
    }
  };

  const clonedVoices = voices.filter((v) => v.category === "cloned");
  const premadeVoices = voices.filter((v) => v.category === "premade");
  const generatedVoices = voices.filter((v) => v.category === "generated");

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Voice Library</h1>
            <p className="mt-2 text-muted-foreground">
              Manage voice clones and text-to-speech voices
            </p>
          </div>
          <Button onClick={() => setCloneDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Clone Voice
          </Button>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Voice Tabs */}
          <Tabs defaultValue="cloned" className="space-y-6">
            <TabsList>
              <TabsTrigger value="cloned">
                My Voices ({clonedVoices.length})
              </TabsTrigger>
              <TabsTrigger value="premade">
                Premade ({premadeVoices.length})
              </TabsTrigger>
              {generatedVoices.length > 0 && (
                <TabsTrigger value="generated">
                  Generated ({generatedVoices.length})
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="cloned">
              {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-40" />
                  ))}
                </div>
              ) : clonedVoices.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {clonedVoices.map((voice) => (
                    <VoiceCard
                      key={voice.voice_id}
                      voice={voice as Voice}
                      onTest={handleTestVoice}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Mic className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">
                      No cloned voices yet
                    </h3>
                    <p className="text-muted-foreground text-center mb-4">
                      Clone a voice from audio samples to create custom voices
                      for your projects.
                    </p>
                    <Button onClick={() => setCloneDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Clone Your First Voice
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="premade">
              {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <Skeleton key={i} className="h-40" />
                  ))}
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {premadeVoices.map((voice) => (
                    <VoiceCard
                      key={voice.voice_id}
                      voice={voice as Voice}
                      onTest={handleTestVoice}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            {generatedVoices.length > 0 && (
              <TabsContent value="generated">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {generatedVoices.map((voice) => (
                    <VoiceCard
                      key={voice.voice_id}
                      voice={voice as Voice}
                      onTest={handleTestVoice}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>
      </main>

      {/* Dialogs */}
      <VoiceCloneDialog
        open={cloneDialogOpen}
        onOpenChange={setCloneDialogOpen}
        onSubmit={handleCloneVoice}
      />

      <TTSTestDialog
        open={ttsDialogOpen}
        onOpenChange={setTtsDialogOpen}
        voice={selectedVoice}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Voice"
        description={`Are you sure you want to delete "${voiceToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
      />
    </div>
  );
}
